import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { DueItem, Unternehmen } from "@kundeninfo/api-client";
import { darfAbbrechen, useTelefon } from "@kundeninfo/ui";
import { Reiter } from "@kundeninfo/ui/me-design";
import { KiRegister } from "../../components/meHuellen.js";
import { TicketBoard, fokussiereKarte } from "./TicketBoard.js";
import { TicketListe } from "./TicketListe.js";
import { TicketTimeline, focusTimelineRow } from "./TicketTimeline.js";
import { TIMELINE_TEXT } from "./timelineTexts.js";
import { TicketDialog } from "./TicketDialog.js";
import type { TicketDialogProps } from "./TicketDialog.js";
import { TicketAnlegen } from "./TicketAnlegen.js";
import { verknuepfungenReiter } from "./TicketVerknuepfungen.js";
import { leseFilter, merkeFilter } from "./boardSpeicher.js";
import type { BoardFilter } from "./boardSpeicher.js";
import { berlinerTag, heuteBeschriftung, tagBeschriftung } from "./ticketFormat.js";
import { hatFremdtickets } from "./fremdSicht.js";
import type { TicketsClient } from "./ticketsClient.js";
import { MilestonePanel } from "../milestones/MilestonePanel.js";
import { readDueSources, rememberDueSources } from "../termine/dueSourceMemory.js";
import { terminBezug } from "../termine/dueItems.js";
import type { DueSource } from "../termine/dueItems.js";
import { oeffneOderSpringe, useObjektPanel } from "../../components/objektPanel/ObjektPanelKontext.js";
import { DUE_ITEM_TEXT } from "../termine/dueItemTexts.js";
import type { TicketView, VerlassenWaechter } from "../../router.js";
import "./Tickets.css";

/**
 * DER BEREICH „TICKETS" (Spec „Board › Aufbau", F43, F129): oben die Reiterzeile Board | Liste | Zeitachse
 * (`me-register`, Adresse F19; Zeitachse: Spec Plan-Ansicht, 532) mit dem Zähler „offen" am Reiter Board (vom Board
 * gemeldet, mit Filtern, 61i) und dem Datum rechts (61o, am Telefon kurz; Board und Zeitachse melden den Tag des
 * Servers, die Liste nimmt den Berliner Tag); darunter das Panel des aktiven Reiters mit seiner
 * Werkzeugleiste. Das Ticketpanel über `/tickets/<id>` bzw. `/tickets/zeitachse/<id>` (Direktaufruf, schließt zurück
 * auf die Ansicht darunter; Paket 1c F121: am Desktop rechts über dem sichtbaren, abgedunkelten Board) und
 * „+ Aufgabe" (in der Werkzeugleiste von Board, Liste bzw. Zeitachse). Nach jeder Sofort-Schreibhandlung im Panel,
 * beim Schließen nach Änderungen und nach dem Anlegen lädt die Ansicht darunter neu (`neuLaden`,
 * Rollposition bleibt, F20). Beim Schließen geht der Fokus an die Karte des Tickets, wenn me-dialog ihn
 * nicht schon an den Auslöser zurückgeben konnte (die Karte kann nach dem Neuladen eine andere sein) — in der
 * Zeitachse an die Zeile des Tickets (`focusTimelineRow`); fehlt sie, an den aktiven Reiter (F129).
 *
 * Die vier Filter teilen Board, Liste und Zeitachse (Ruling A4, S16) über EIN Gedächtnis (`boardSpeicher.ts`):
 * Das Board führt und merkt sie selbst; Liste und Zeitachse bekommen sie von hier (`filter`/`onFilter`).
 * Der Bereich hält ihren Filter im Speicher der Sitzung und übernimmt beim Wechsel vom
 * Board nur, was das Board im Gedächtnis GEÄNDERT hat — so überlebt die Wahl auch
 * ohne Browserspeicher (privates Fenster), und eine Wahl im Board kommt in Liste und Zeitachse an. „+n“ in der
 * Zeitachse wechselt in die Liste mit Zuständig = Person (übrige Filter bleiben, gemerkt wie jede Filterwahl).
 *
 * Der Nebendialog (Spec Teilaufgaben R23, Festlegung 24; Öffnen statt Springen F14): Eine Zeile im Abschnitt „Teilaufgaben“
 * und der Panelknopf des Elterntickets öffnen das andere Ticket als zweiten `TicketDialog` seitlich neben dem Dialog der
 * Adresse; die Adresse bleibt. Schreibt er, zeichnen der Dialog darunter (`reloadToken`) und die Ansicht nach. Nennt er das
 * Ticket der Adresse, schließt er (`belowId`); ein anderes Ticket ersetzt ihn — mit Rückfrage, wenn dort Ungespeichertes
 * steht; der Verlassen-Wächter zählt seine Eingaben mit. Wechselt die Adresse (auch über den Sprungknopf), schließt er mit.
 * Er trägt kein `verlauf`: Der Sprungknopf wechselt die Adresse, und ein Verlaufseintrag nähme diesen Wechsel beim Schließen
 * wieder zurück.
 *
 * „Im Board zeigen“ (Spec Teilaufgaben R26; Abschnitt „Teilaufgaben“ beider Dialoge, Randliste am Telefon) schließt die
 * Dialoge — mit Rückfrage bei Ungespeichertem —, wechselt auf das Board und reicht ihm die Bitte `focusRequest`.
 *
 * Terminebene (Spec D22, D23; Plan T17, T20): Der Bereich hält die Quellenwahl (gemerkt je Benutzer unter
 * `ki-due-sources:<userId>`, gemeinsam für Liste und Zeitachse) und führt die Sprünge der Termine aus — ein Ticket öffnet
 * den Ticketdialog der Adresse, ein Lieferantenobjekt sein Objektpanel, ein Beleg springt in die Akte, ein Meilenstein
 * öffnet sein Panel (Aufgabe 8, D21). „+ Meilenstein“ (Zeitachse, Abschnitt „Termine“) öffnet es neu mit dem Unternehmen
 * des Filters, bei „Alle“ mit dem Arbeitsunternehmen. Aus dem Reiter „Verknüpfungen“ liegt es seitlich über dem
 * Ticketdialog bzw. Nebendialog; hat es geschrieben, lädt genau dieser Dialog nach (`reloadToken`). Nach jedem Schließen
 * mit Änderung lädt die Ansicht darunter neu; Eingaben im Panel zählen für den Verlassen-Wächter. Schließt das Panel nach
 * seiner eigenen Frage, gibt es den Weg frei, nur wenn sonst nichts Ungespeichertes offen ist (Review M1).
 *
 * Der Dialog trägt die Ticket-Kennung als `key`: Wechselt die Adresse auf ein anderes Ticket,
 * beginnen Entwürfe und Startversion frisch (Review 15). Weil er an der Adresse hängt, fragt
 * ein Verlassen-Wächter (Hausmuster, `registriereVerlassenWaechter`) gestapelt nach, solange
 * im Dialog oder in „Aufgabe anlegen“ Ungespeichertes steht — Zurück-Taste, Bereichswechsel
 * und Ansichtswechsel verwerfen sonst still (Review 17). Schließt der Dialog selbst (er hat
 * dann schon gefragt), gibt der Bereich den Weg frei, damit nicht zweimal gefragt wird.
 */
export type TicketsRoute = { ansicht: "board" | TicketView; id: string | null };

/** Kennungen der Reiter und ihrer Panels (F129: `fuer` am Reiter, `id` am Panel). */
const PANEL = { board: "tickets-board", liste: "tickets-liste", zeitachse: "tickets-timeline" } as const;
/** Die Sektion jeder Ansicht: Fokus-Ersatz, wenn der Reiter keinen Fokus nimmt (F129). */
const SECTION = { board: "section.tb", liste: "section.tl", zeitachse: "section.tl-zeitachse" } as const;
const VIEWS = Object.keys(PANEL) as TicketsRoute["ansicht"][];

export type BereichTicketsProps = {
  client: TicketsClient; companies: readonly Unternehmen[];
  ich: { id: string; name: string; istAdmin: boolean; supplierView?: boolean };
  arbeitsunternehmenId: string | null; route: TicketsRoute; onNavigiere: (r: TicketsRoute) => void;
  zusatzReiter?: TicketDialogProps["zusatzReiter"];
  registriereVerlassenWaechter?: (frage: VerlassenWaechter) => () => void;
};

function gleicherFilter(a: BoardFilter, b: BoardFilter): boolean {
  return a.company === b.company && a.customer === b.customer && a.assignee === b.assignee && a.linked === b.linked
    && (a.team ?? "") === (b.team ?? "");
}

export function BereichTickets(p: BereichTicketsProps): ReactElement {
  const [neuLaden, setNeuLaden] = useState(0);
  const neuLadenAnstossen = useCallback(() => setNeuLaden((n) => n + 1), []);
  const telefon = useTelefon();
  // F121: Schließen gibt den Fokus an die Karte zurück, sofern sie noch da ist. me-dialog gibt ihn
  // selbst an den Auslöser zurück; hat das Board die Karte nach einer Änderung neu gezeichnet
  // (andere Spalte) oder wurde das Ticket direkt aufgerufen, stünde er sonst auf <body>. Wandert
  // die Karte erst mit dem Neuladen nach dem Schließen, holt `TicketBoard` den Fokus nach. Fehlt
  // die Karte (Filter, anderes Unternehmen, Liste), geht er an den aktiven Reiter, ohne zu rollen;
  // nimmt der keinen Fokus an, an die Sektion der Ansicht (F129). In der Zeitachse an die Zeile, die das Ticket trägt
  // (Spec Plan-Ansicht „Fokus-Rückgabe“) — nur Tickets im Fenster haben eine; sonst der Reiter.
  const offenesTicket = useRef<string | null>(null);
  useEffect(() => {
    const vorher = offenesTicket.current;
    offenesTicket.current = p.route.id;
    if (vorher === null || p.route.id !== null) return;
    const aktiv = document.activeElement;
    if (aktiv !== null && aktiv !== document.body) return;
    if ((p.route.ansicht === "zeitachse" ? focusTimelineRow : fokussiereKarte)(document, vorher)) return;
    document.querySelector<HTMLElement>(`me-reiter[fuer="${PANEL[p.route.ansicht]}"]`)?.focus({ preventScroll: true });
    const jetzt = document.activeElement;
    if (jetzt !== null && jetzt !== document.body) return;
    document.querySelector<HTMLElement>(SECTION[p.route.ansicht])?.focus({ preventScroll: true });
  }, [p.route.id, p.route.ansicht]);
  // F129 (61c, 61i): Zähler „offen“ (mit Filtern) und der Tag des Servers kommen vom Board bzw. der Zeitachse (sie lädt
  // das Board); der Zähler steht nur am Reiter Board. Die Liste lädt das Board nicht — dort ist das Datum der Berliner
  // Tag (F13).
  const [kopf, setKopf] = useState<{ offen: number; heute: string } | null>(null);
  const offenGemeldet = useCallback((offen: number, heute: string) => {
    setKopf((alt) => (alt !== null && alt.offen === offen && alt.heute === heute ? alt : { offen, heute }));
  }, []);
  /** „Aufgabe anlegen“; `due` ist der Termin, aus dem es entsteht („Ticket anlegen“ am Termin, Aufgabe 12). */
  const [anlegen, setAnlegen] = useState<{ companyId: string | null; due?: DueItem } | null>(null);
  /** Terminebene H10: das Ticket, das nach 409 statt eines neuen aufging — sein Dialog sagt es, bis die Adresse ein anderes
   *  oder keines nennt (Muster „Zustand bei geänderter Eingabe zurücksetzen“). */
  const [takenTicket, setTakenTicket] = useState<string | null>(null);
  const [routeTicketSeen, setRouteTicketSeen] = useState(p.route.id);
  if (routeTicketSeen !== p.route.id) {
    setRouteTicketSeen(p.route.id);
    if (p.route.id !== takenTicket) setTakenTicket(null);
  }
  const companyIds = p.companies.map((c) => c.id);
  const [filter, setFilter] = useState<BoardFilter>(() => leseFilter(companyIds));
  // Das Gedächtnis beim Betreten des Boards: Beim Zurück in die Liste zählt nur, was das Board
  // daran geändert hat. Abgeglichen wird noch im selben Zeichnen (Muster „Zustand bei
  // geänderter Eingabe zurücksetzen"), damit die Liste gleich mit dem richtigen Filter lädt.
  const [beimBoard, setBeimBoard] = useState<BoardFilter | null>(() =>
    (p.route.ansicht === "board" ? leseFilter(companyIds) : null));
  const [ansichtVorher, setAnsichtVorher] = useState(p.route.ansicht);
  // R26: die Bitte „Im Board zeigen" an das Board; sie gilt für das Board, das sie zeigt — die Liste räumt sie ab, sonst
  // stünde der alte Fokus beim nächsten Wechsel aufs Board wieder da.
  const [focusRequest, setFocusRequest] = useState<{ id: string; seq: number } | null>(null);
  if (ansichtVorher !== p.route.ansicht) {
    setAnsichtVorher(p.route.ansicht);
    if (p.route.ansicht !== "board") setFocusRequest(null);
    // Eine Zahl aus einem älteren Stand wäre falsch (F129): Der Zähler kommt neu, sobald das Board lädt.
    setKopf(null);
    const jetzt = leseFilter(companyIds);
    if (p.route.ansicht === "board") setBeimBoard(jetzt);
    // Zwischen Liste und Zeitachse gilt der Filter des Bereichs weiter (kein Gedächtnis nötig, A4).
    else if (ansichtVorher === "board" && (beimBoard === null || !gleicherFilter(jetzt, beimBoard))) setFilter(jetzt);
  }

  // Ungespeichertes im Dialog (je Kennung) bzw. in „Aufgabe anlegen“ — nur solange das
  // Gemeldete zum Offenen passt (ein ausgehängter Dialog meldet nichts mehr).
  const [dialogOffen, setDialogOffen] = useState<{ id: string; ja: boolean }>({ id: "", ja: false });
  const [anlegenOffen, setAnlegenOffen] = useState(false);
  // Der Nebendialog (Festlegung 24, F14), seine Eingaben (`nebenOffen`) und der Zähler seiner Schreibhandlungen; er gehört zur
  // Adresse und schließt, wenn sie wechselt (Muster „Zustand bei geänderter Eingabe zurücksetzen“ — im selben Zeichnen,
  // kein Bild mit ihm über dem neuen Ticket; der neue Dialog der Adresse beginnt ohne Nachladen).
  const [neben, setNeben] = useState<string | null>(null);
  const [nebenOffen, setNebenOffen] = useState(false);
  const [nebenZug, setNebenZug] = useState(0);
  /** Nachladen des Nebendialogs selbst — wenn das Meilenstein-Panel aus SEINEM Reiter „Verknüpfungen“ schrieb (Review G2). */
  const [nebenNachladen, setNebenNachladen] = useState(0);
  const zeigeNeben = (id: string | null) => { setNeben(id); setNebenOffen(false); setNebenNachladen(0); };
  const [nebenAdresse, setNebenAdresse] = useState(p.route.id);
  if (nebenAdresse !== p.route.id) {
    setNebenAdresse(p.route.id);
    zeigeNeben(null);
    setNebenZug(0);
  }
  // Das Meilenstein-Panel (Aufgabe 8): neu (`id: null`) oder vorhanden; `fromTicket` = aus dem Reiter „Verknüpfungen“
  // dieses Tickets geöffnet (Haupt- oder Nebendialog) — dann liegt es seitlich darüber und lädt genau diesen Dialog nach.
  const [milestone, setMilestone] = useState<{ id: string | null; companyId: string | null; fromTicket: string | null } | null>(null);
  const [milestoneOffen, setMilestoneOffen] = useState(false);
  const dialogUngespeichert = p.route.id !== null && dialogOffen.id === p.route.id && dialogOffen.ja;
  /** Ungespeichertes außerhalb des Meilenstein-Panels (Dialog, Nebendialog, „Aufgabe anlegen“). */
  const sonstUngespeichert = dialogUngespeichert || (anlegen !== null && anlegenOffen) || (neben !== null && nebenOffen);
  const ungespeichert = sonstUngespeichert || (milestone !== null && milestoneOffen);
  const freigabe = useRef(false);
  const registriere = p.registriereVerlassenWaechter;
  useEffect(() => {
    if (!ungespeichert || registriere === undefined) return undefined;
    freigabe.current = false;
    return registriere(() => (freigabe.current ? true : darfAbbrechen(true)));
  }, [ungespeichert, registriere]);
  const dialogMeldet = useCallback((ja: boolean) => {
    if (p.route.id !== null) setDialogOffen({ id: p.route.id, ja });
  }, [p.route.id]);
  /** Ein Weg, den der Dialog selbst beschlossen hat (Speichern, Abbrechen nach seiner Frage,
   *  Anlegen): ohne zweite Frage. */
  const freigeben = () => {
    freigabe.current = true;
    setDialogOffen({ id: "", ja: false });
    setAnlegenOffen(false);
  };

  // In Board und Zeitachse der Tag des Servers (Berliner Tag aus `board.heute`), sonst der Berliner Tag der Uhr.
  const heute = p.route.ansicht !== "liste" && kopf !== null ? kopf.heute : berlinerTag(new Date().toISOString());
  const oeffnen = (id: string) => p.onNavigiere({ ...p.route, id });
  // D22: eine Quellenwahl für Liste und Zeitachse, gemerkt je Benutzer.
  const [dueSources, setDueSources] = useState<DueSource[]>(() => readDueSources(p.ich.id));
  const dueSourcesSetzen = (s: DueSource[]) => { setDueSources(s); rememberDueSources(p.ich.id, s); };
  const panels = useObjektPanel();
  /** T20: Ticket → Ticketdialog, Lieferantenobjekt → Objektpanel, Beleg → Akte (`oeffneOderSpringe`), Meilenstein → Panel
   *  (D21). Schrieb das Objektpanel, lädt der Bereich im Stehen nach (F9). */
  const oeffneTermin = (item: DueItem) => {
    const t = item.target;
    if (t.kind === "TICKET") oeffnen(t.ticketId);
    else if (t.kind === "RECORD") oeffneOderSpringe(panels, terminBezug(t, item), { onGeaendert: neuLadenAnstossen });
    else setMilestone({ id: t.milestoneId, companyId: item.companyId, fromTicket: null });
  };
  /** „+ Meilenstein“: Unternehmen der Ansicht (Filter), bei „Alle“ das Arbeitsunternehmen. */
  const neuerMeilenstein = (companyId: string | null) =>
    setMilestone({ id: null, companyId: companyId ?? p.arbeitsunternehmenId, fromTicket: null });
  /** R26: „Im Board zeigen" — Dialoge zu (Rückfrage bei Ungespeichertem), Board, Fokus, Hauptkarte ins Bild. */
  const imBoardZeigen = async (id: string) => {
    if (ungespeichert && !(await darfAbbrechen(true))) return;
    freigeben();
    zeigeNeben(null);
    setFocusRequest((alt) => ({ id, seq: (alt?.seq ?? 0) + 1 }));
    p.onNavigiere({ ansicht: "board", id: null });
  };
  const nebenGeschrieben = () => { setNebenZug((n) => n + 1); neuLadenAnstossen(); };
  /** Das Meilenstein-Panel schließt — nach seiner eigenen Frage, auch bevor es ein Ticket öffnet (K10). */
  const meilensteinZu = (changed: boolean) => {
    if (milestone === null) return;
    // Das Panel hat selbst gefragt: ohne zweite Frage — aber nur, wenn sonst nichts Ungespeichertes offen ist
    // (der Wächter liest synchron; ein Dialog darunter bleibt geschützt, Review M1).
    if (!sonstUngespeichert) freigabe.current = true;
    setMilestone(null);
    setMilestoneOffen(false);
    if (!changed) return;
    if (milestone.fromTicket !== null && milestone.fromTicket === neben) setNebenNachladen((n) => n + 1);
    else if (milestone.fromTicket !== null) setNebenZug((n) => n + 1);
    neuLadenAnstossen();
  };
  // Pfad und Reiter EINMAL (Pre-flight C05) — beide Dialoge nehmen sie, der Nebendialog ist ein Ticket wie jedes.
  const pfadText = { board: "Tickets › Board", liste: "Tickets › Liste", zeitachse: TIMELINE_TEXT.path }[p.route.ansicht];
  const panelName = { board: "Board", liste: "Liste", zeitachse: TIMELINE_TEXT.tab }[p.route.ansicht];
  /** „+n“ der Zeitachse (S16): die Liste mit Zuständig = Person — gemerkt wie jede Filterwahl. */
  const zurListe = (f: BoardFilter) => {
    setFilter(f);
    merkeFilter(f);
    p.onNavigiere({ ansicht: "liste", id: null });
  };
  const zusatzReiter: TicketDialogProps["zusatzReiter"] = [...(p.zusatzReiter ?? []),
    verknuepfungenReiter(p.client, (d, m) => setMilestone({ id: m.id, companyId: d.companyId, fromTicket: d.id }))];
  return (
    <div className="bt">
      <KiRegister className="bt__reiter" aria-label="Ansicht" aktiv={PANEL[p.route.ansicht]}
        onReiterGewaehlt={(d) => p.onNavigiere({ ansicht: VIEWS.find((v) => PANEL[v] === d.id) ?? "board", id: null })}
        rechts={(
          <span className="bt__heute">
            {telefon
              ? <><span className="bt__unsichtbar">Heute </span>{tagBeschriftung(heute)}</>
              : `Heute ${heuteBeschriftung(heute)}`}
          </span>
        )}>
        <Reiter fuer={PANEL.board} beschriftung="Board"
          {...(p.route.ansicht === "board" && kopf !== null ? { zaehler: kopf.offen, zaehlerText: "offen" } : {})} />
        <Reiter fuer={PANEL.liste} beschriftung="Liste" />
        <Reiter fuer={PANEL.zeitachse} beschriftung={TIMELINE_TEXT.tab} />
      </KiRegister>
      {/* Das Panel des aktiven Reiters (Hausregel „Reiter nach unten offen“): die Ansicht samt Leiste. */}
      <div className="bt__panel" id={PANEL[p.route.ansicht]} role="tabpanel" aria-label={panelName}>
        {p.route.ansicht === "zeitachse"
          ? <TicketTimeline client={p.client} companies={p.companies} ich={p.ich} onOeffnen={oeffnen}
              onAnlegen={(companyId) => setAnlegen({ companyId })} neuLaden={neuLaden} filter={filter} onFilter={setFilter}
              onOffen={offenGemeldet} onZurListe={zurListe}
              dueSources={dueSources} onDueSources={dueSourcesSetzen} onOpenDue={oeffneTermin}
              supplierView={p.ich.supplierView === true} onCreateMilestone={neuerMeilenstein} />
          : p.route.ansicht === "board"
          ? <TicketBoard client={p.client} companies={p.companies} ich={p.ich} onOeffnen={oeffnen}
              {...(hatFremdtickets(p.client) ? { fremd: p.client } : {})}
              onAnlegen={(companyId) => setAnlegen({ companyId })} neuLaden={neuLaden} onOffen={offenGemeldet}
              focusRequest={focusRequest} onShowInBoard={(id) => { void imBoardZeigen(id); }} />
          : <TicketListe client={p.client} companies={p.companies} onOeffnen={oeffnen} ichId={p.ich.id}
              ichIstAdmin={p.ich.istAdmin} ichName={p.ich.name} {...(hatFremdtickets(p.client) ? { fremd: p.client } : {})}
              neuLaden={neuLaden} filter={filter} onFilter={setFilter}
              onAnlegen={() => setAnlegen({ companyId: null })}
              dueSources={dueSources} onDueSources={dueSourcesSetzen} onOpenDue={oeffneTermin}
              onCreateMilestone={() => neuerMeilenstein(filter.company === "ALLE" ? null : filter.company)}
              onCreateTicket={(item) => setAnlegen({ companyId: item.companyId, due: item })}
              supplierView={p.ich.supplierView === true} />}
      </div>
      {anlegen !== null && (
        <TicketAnlegen client={p.client} companies={p.companies} ich={p.ich} vorgabeCompanyId={anlegen.companyId}
          arbeitsunternehmenId={p.arbeitsunternehmenId} onUngespeichert={setAnlegenOffen}
          key={anlegen.due?.key ?? "neu"} {...(anlegen.due === undefined ? {} : { dueOrigin: { item: anlegen.due } })}
          onVorhanden={(id) => { freigeben(); setAnlegen(null); setTakenTicket(id); setNeuLaden((n) => n + 1); oeffnen(id); }}
          onAbbrechen={() => { freigeben(); setAnlegen(null); }}
          onAngelegt={(id) => { freigeben(); setAnlegen(null); setNeuLaden((n) => n + 1); oeffnen(id); }} />
      )}
      {p.route.id !== null && (
        <TicketDialog key={p.route.id} client={p.client} ticketId={p.route.id} companies={p.companies}
          ich={{ id: p.ich.id, istAdmin: p.ich.istAdmin, supplierView: p.ich.supplierView === true }}
          pfad={pfadText} reloadToken={nebenZug} onShowInBoard={(id) => { void imBoardZeigen(id); }}
          onOpenTicket={zeigeNeben}
          onUngespeichert={dialogMeldet}
          onGeaendert={neuLadenAnstossen}
          zusatzReiter={zusatzReiter}
          {...(takenTicket === p.route.id ? { notice: DUE_ITEM_TEXT.originTakenOpened } : {})}
          onSchliessen={(geaendert) => {
            freigeben();
            if (geaendert) setNeuLaden((n) => n + 1);
            p.onNavigiere({ ...p.route, id: null });
          }} />
      )}
      {p.route.id !== null && neben !== null && (
        <TicketDialog key={`neben-${neben}`} client={p.client} ticketId={neben} companies={p.companies} unter="seitlich"
          belowId={p.route.id} ich={{ id: p.ich.id, istAdmin: p.ich.istAdmin, supplierView: p.ich.supplierView === true }}
          pfad={pfadText} zusatzReiter={zusatzReiter} onGeaendert={nebenGeschrieben} onUngespeichert={setNebenOffen}
          reloadToken={nebenNachladen}
          onShowInBoard={(id) => { void imBoardZeigen(id); }}
          // Ein weiteres Ticket (Enkel, Eltern des Elterntickets) ersetzt den Nebendialog — Ungespeichertes dort erst nach der
          // Rückfrage (Review 17); das Ticket der Adresse schließt ihn (`belowId`, im Dialog).
          onOpenTicket={(id) => { void darfAbbrechen(nebenOffen).then((ja) => { if (ja) zeigeNeben(id); }); }}
          onSchliessen={(geaendert) => { zeigeNeben(null); if (geaendert) nebenGeschrieben(); }} />
      )}
      {milestone !== null && (
        <MilestonePanel key={milestone.id ?? "neu"} client={p.client} companies={p.companies} milestoneId={milestone.id}
          defaultCompanyId={milestone.companyId} {...(milestone.fromTicket === null ? {} : { unter: "seitlich" as const })}
          onUngespeichert={setMilestoneOffen} onOpenTicket={(id, changed) => { meilensteinZu(changed); oeffnen(id); }}
          onClose={meilensteinZu} />
      )}
    </div>
  );
}
